import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia
} from '@mui/material';
import { useData } from '../../context/DataContext';
import { Link as RouterLink } from 'react-router-dom';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import HealingIcon from '@mui/icons-material/Healing';
import BiotechIcon from '@mui/icons-material/Biotech';
import HeroSlider from './HeroSlider';
import PostCard from './PostCard';
import { FEATURED_HOME_SERVICES, getServiceImageUrl, getServiceRoute } from '../../utils/serviceImages';
import apiClient from '../../services/api';

// Fallback content: used until the CMS-backed data loads, and as a safety net if either
// request ever fails or returns nothing - the homepage must never break.
const DEFAULT_CTA = {
  heading: 'Ready to Get Started?',
  subtitle: 'Schedule an appointment or contact us for more information. Your health journey starts here.',
  button1Text: 'Schedule Appointment',
  button1Link: '/contact',
  button2Text: 'Join Our Team',
  button2Link: '/careers',
};

const Home = () => {
  const { state } = useData();
  const { posts } = state;

  const [featuredServices, setFeaturedServices] = useState(FEATURED_HOME_SERVICES);
  const [cta, setCta] = useState(DEFAULT_CTA);

  // Hero slider is now handled by the HeroSlider component

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await apiClient.getFeaturedServices();
        if (cancelled || !result || !result.success || !Array.isArray(result.data)) return;
        const active = result.data.filter((s) => s.isActive !== false);
        if (active.length > 0) setFeaturedServices(active);
      } catch (e) {
        // Network/API issue - keep showing FEATURED_HOME_SERVICES, no user-facing error.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await apiClient.getHospitalInfo();
        if (cancelled || !result || !result.success || !result.data) return;
        const d = result.data;
        setCta({
          heading: d.homeCtaHeading || DEFAULT_CTA.heading,
          subtitle: d.homeCtaSubtitle || DEFAULT_CTA.subtitle,
          button1Text: d.homeCtaButton1Text || DEFAULT_CTA.button1Text,
          button1Link: d.homeCtaButton1Link || DEFAULT_CTA.button1Link,
          button2Text: d.homeCtaButton2Text || DEFAULT_CTA.button2Text,
          button2Link: d.homeCtaButton2Link || DEFAULT_CTA.button2Link,
        });
      } catch (e) {
        // Network/API issue - keep showing DEFAULT_CTA, no user-facing error.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const getServiceIcon = (iconName) => {
    switch (iconName) {
      case 'emergency': return <LocalHospitalIcon />;
      case 'medical_services': return <MedicalServicesIcon />;
      case 'healing': return <HealingIcon />;
      case 'biotech': return <BiotechIcon />;
      default: return <LocalHospitalIcon />;
    }
  };

  return (
    <Box>
      {/* Hero Slider */}
      <HeroSlider />

      {/* Featured Services */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, sm: 8, md: 12 } }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 6, md: 8 } }}>
          <Typography variant="h2" component="h2" gutterBottom sx={{ 
            fontWeight: 700, 
            mb: { xs: 1, sm: 2 },
            fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' },
            lineHeight: 1.2
          }}>
            Our Services
          </Typography>
          <Typography variant="h5" sx={{ 
            color: 'text.secondary', 
            maxWidth: 600, 
            mx: 'auto',
            fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
            lineHeight: 1.4
          }}>
            Comprehensive healthcare services designed to meet all your medical needs
          </Typography>
        </Box>
        <Grid container spacing={4}>
          {featuredServices.map((service) => (
            <Grid item xs={12} sm={6} key={service.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  borderRadius: 3,
                  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0px 20px 40px rgba(0, 0, 0, 0.12)',
                  },
                }}
              >
                <CardMedia
                  component="img"
                  height="240"
                  image={getServiceImageUrl(service)}
                  alt={service.name}
                  sx={{ objectFit: 'cover' }}
                />
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 2,
                        bgcolor: 'primary.main',
                        color: 'white',
                        mr: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 40,
                        minHeight: 40,
                      }}
                    >
                      {getServiceIcon(service.icon)}
                    </Box>
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {service.name}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ flexGrow: 1, mb: 3, lineHeight: 1.6 }}
                  >
                    {service.description}
                  </Typography>
                  <Button
                    component={RouterLink}
                    to={getServiceRoute(service)}
                    variant="outlined"
                    color="primary"
                    sx={{
                      alignSelf: 'flex-start',
                      fontWeight: 500,
                      borderRadius: 2,
                      px: 2.5,
                      '&:hover': {
                        transform: 'translateX(4px)',
                      },
                      transition: 'transform 0.2s ease',
                    }}
                  >
                    Learn More →
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

      </Container>

      {/* Latest News */}
      {Array.isArray(posts) && posts.length > 0 && (
        <Container maxWidth="lg" sx={{ py: 12 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h2" component="h2" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
              Latest News & Updates
            </Typography>
            <Typography variant="h5" sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto' }}>
              Stay informed about the latest developments in healthcare and our hospital
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {posts.slice(0, 3).map((post) => (
              <Grid item xs={12} md={4} key={post.id}>
                <PostCard post={post} />
              </Grid>
            ))}
          </Grid>
        </Container>
      )}

      {/* Call to Action */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 50%, #8b5cf6 100%)',
        color: 'white', 
        py: 12,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.3,
        },
      }}>
        <Container maxWidth="lg" sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <Typography variant="h2" component="h2" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
            {cta.heading}
          </Typography>
          <Typography variant="h5" sx={{ mb: 6, opacity: 0.95, maxWidth: 600, mx: 'auto' }}>
            {cta.subtitle}
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              component={RouterLink}
              to={cta.button1Link}
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: '#2563eb',
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: '#f8fafc',
                  transform: 'translateY(-2px)',
                  boxShadow: '0px 8px 25px rgba(0,0,0,0.15)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {cta.button1Text}
            </Button>
            <Button
              component={RouterLink}
              to={cta.button2Link}
              variant="outlined"
              size="large"
              sx={{
                borderColor: 'white',
                color: 'white',
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {cta.button2Text}
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Home; 